import React, { Component } from 'react'
import Context from './Context'
import { TELEGRAM_THEME_VARS, mergeClasses } from './telegramTheme'
import TelegramButton from './TelegramButton'
import TelegramChip from './TelegramChip'
import TelegramBadge from './TelegramBadge'
import './AiSettingsPopup.css'

const I18N = {
  ru: {
    title: 'Настройки AI-функций',
    subtitle: 'Настройте модель, поведение и доступ к инструментам.',
    modelSection: 'Модель',
    modelHint: 'Выберите пресет, который лучше подходит под ваш сценарий.',
    modelFast: 'Быстрый',
    modelBalanced: 'Баланс',
    modelCreative: 'Креатив',
    featuresSection: 'Возможности',
    memoryTitle: 'Долгая память',
    memoryDesc: 'Сохраняет контекст между сессиями.',
    toolsTitle: 'Инструменты',
    toolsDesc: 'Калькулятор, код и системные действия.',
    webTitle: 'Веб-поиск',
    webDesc: 'Актуальные ответы с внешних источников.',
    on: 'Включено',
    off: 'Отключено',
    safetySection: 'Ограничения',
    safetyHint: 'Политики ограничивают рискованные действия.',
    restrictedTitle: 'Режим только чтение',
    restrictedText: 'Изменение системных настроек недоступно в этой среде.',
    draft: 'Черновик',
    saving: 'Сохранение',
    saved: 'Сохранено',
    readOnly: 'Read-only',
    estCost: 'Оценка стоимости: ~0.14 credits / request',
    cancel: 'Отмена',
    reset: 'Сброс',
    save: 'Сохранить',
    savedButton: 'Сохранено',
  },
  en: {
    title: 'AI Features Settings',
    subtitle: 'Configure model behavior and tool access.',
    modelSection: 'Model preset',
    modelHint: 'Pick the profile that best matches your workflow.',
    modelFast: 'Fast',
    modelBalanced: 'Balanced',
    modelCreative: 'Creative',
    featuresSection: 'Capabilities',
    memoryTitle: 'Long-term memory',
    memoryDesc: 'Keeps context between sessions.',
    toolsTitle: 'Tools',
    toolsDesc: 'Calculator, code actions and utility tools.',
    webTitle: 'Web search',
    webDesc: 'Fetches up-to-date external information.',
    on: 'Enabled',
    off: 'Disabled',
    safetySection: 'Restrictions',
    safetyHint: 'Policy controls can limit risky actions.',
    restrictedTitle: 'Read-only mode',
    restrictedText: 'System-level changes are blocked in this environment.',
    draft: 'Draft',
    saving: 'Saving',
    saved: 'Saved',
    readOnly: 'Read-only',
    estCost: 'Estimated cost: ~0.14 credits / request',
    cancel: 'Cancel',
    reset: 'Reset',
    save: 'Save changes',
    savedButton: 'Saved',
  }
}

class AiSettingsPopup extends Component {
  static contextType = Context

  locale () {
    const locale = this.props.locale || (this.context && this.context.locale)
    return locale === 'ru' ? 'ru' : 'en'
  }

  featureRow (key, title, desc, enabled, onClick, disabled) {
    const locale = this.locale()
    const t = I18N[locale]

    return (
      <div className='AiSettingsRow' key={ key }>
        <div className='AiSettingsRowText'>
          <span className='AiSettingsRowTitle'>{ title }</span>
          <span className={ mergeClasses([
            'AiSettingsRowDesc',
            this.context && this.context.theme === 'dark' ? 'AiSettingsRowDesc--dark' : null
          ]) }>{ desc }</span>
        </div>

        <div className='AiSettingsRowControl'>
          <TelegramButton
            bare
            size='s'
            mode={ enabled ? 'filled' : 'gray' }
            disabled={ disabled }
            onClick={ onClick }
          >
            { enabled ? t.on : t.off }
          </TelegramButton>
        </div>
      </div>
    )
  }

  render () {
    const locale = this.locale()
    const t = I18N[locale]
    const theme = this.context && this.context.theme === 'dark' ? 'dark' : 'light'

    const {
      popupState = 'draft',
      model = 'balanced',
      memoryEnabled = true,
      toolsEnabled = true,
      webEnabled = true,
      showSafetyWarning = false,
      note,
      onCancel,
      onReset,
      onSave,
      onMemoryClick,
      onToolsClick,
      onWebClick,
    } = this.props

    const statusProps = {
      type: 'number',
      value: 'AI',
      mode: 'gray',
      label: t.draft
    }

    if (popupState === 'saving') {
      statusProps.mode = 'secondary'
      statusProps.label = t.saving
    } else if (popupState === 'saved') {
      statusProps.mode = 'primary'
      statusProps.label = t.saved
    } else if (popupState === 'restricted') {
      statusProps.mode = 'critical'
      statusProps.label = t.readOnly
    }

    const wrapperClassName = mergeClasses([
      'AiSettingsPopup',
      theme === 'dark' ? 'AiSettingsPopup--dark' : 'AiSettingsPopup--light'
    ])

    const subtitleClassName = mergeClasses([
      'AiSettingsSubtitle',
      theme === 'dark' ? 'AiSettingsSubtitle--dark' : null
    ])

    const hintClassName = mergeClasses([
      'AiSettingsHint',
      theme === 'dark' ? 'AiSettingsHint--dark' : null
    ])

    const metaClassName = mergeClasses([
      'AiSettingsMeta',
      theme === 'dark' ? 'AiSettingsMeta--dark' : null
    ])

    const saveDisabled = popupState === 'restricted'
    const capabilitiesDisabled = popupState === 'restricted'

    return (
      <section className={ wrapperClassName } style={{ ...TELEGRAM_THEME_VARS[theme] }}>
        <header className='AiSettingsHeader'>
          <div>
            <h3 className='AiSettingsTitle'>{ t.title }</h3>
            <p className={ subtitleClassName }>{ t.subtitle }</p>
          </div>

          <TelegramBadge
            type='number'
            mode={ statusProps.mode }
            value={ statusProps.value }
            label={ statusProps.label }
          ></TelegramBadge>
        </header>

        <section className='AiSettingsSection'>
          <h4 className='AiSettingsSectionTitle'>{ t.modelSection }</h4>
          <div className={ hintClassName }>{ t.modelHint }</div>
          <div className='AiSettingsModelGrid'>
            <TelegramChip bare mode={ model === 'fast' ? 'elevated' : 'outline' }>{ t.modelFast }</TelegramChip>
            <TelegramChip bare mode={ model === 'balanced' ? 'elevated' : 'outline' }>{ t.modelBalanced }</TelegramChip>
            <TelegramChip bare mode={ model === 'creative' ? 'elevated' : 'outline' }>{ t.modelCreative }</TelegramChip>
          </div>
        </section>

        <section className='AiSettingsSection'>
          <h4 className='AiSettingsSectionTitle'>{ t.featuresSection }</h4>
          { this.featureRow('memory', t.memoryTitle, t.memoryDesc, memoryEnabled, onMemoryClick, capabilitiesDisabled) }
          { this.featureRow('tools', t.toolsTitle, t.toolsDesc, toolsEnabled, onToolsClick, capabilitiesDisabled) }
          { this.featureRow('web', t.webTitle, t.webDesc, webEnabled, onWebClick, capabilitiesDisabled) }
        </section>

        <section className='AiSettingsSection'>
          <h4 className='AiSettingsSectionTitle'>{ t.safetySection }</h4>
          <div className={ hintClassName }>{ t.safetyHint }</div>

          { showSafetyWarning &&
            <div className='AiSettingsWarning'>
              <p className='AiSettingsWarningTitle'>{ t.restrictedTitle }</p>
              <p className='AiSettingsWarningText'>{ t.restrictedText }</p>
            </div>
          }
        </section>

        <div className='AiSettingsMetaRow'>
          <span className={ metaClassName }>{ note || t.estCost }</span>
        </div>

        <footer className='AiSettingsFooter'>
          <TelegramButton bare mode='plain' onClick={ onCancel }>{ t.cancel }</TelegramButton>
          <TelegramButton bare mode='bezeled' onClick={ onReset } disabled={ popupState === 'saving' }>{ t.reset }</TelegramButton>
          <TelegramButton
            bare
            mode='filled'
            onClick={ onSave }
            loading={ popupState === 'saving' }
            disabled={ saveDisabled }
          >
            { popupState === 'saved' ? t.savedButton : t.save }
          </TelegramButton>
        </footer>
      </section>
    )
  }
}

export default AiSettingsPopup
